import { useState, useEffect, useRef, memo } from 'react';

interface LazyImageProps {
    src: string;
    alt: string;
    className?: string;
    width?: number;
    height?: number;
    /**
     * Clase CSS opcional para el contenedor.
     * Úsala para controlar aspect-ratio, tamaño, etc. desde el componente padre.
     */
    containerClassName?: string;
}

export const LazyImage = memo(({
    src,
    alt,
    className = '',
    width,
    height,
    containerClassName = '',
}: LazyImageProps) => {
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setImageSrc(src);
                    observer.disconnect();
                }
            },
            { rootMargin: '100px' }
        );

        if (imgRef.current) {
            observer.observe(imgRef.current);
        }

        return () => observer.disconnect();
    }, [src]);

    return (
        // El contenedor reserva el espacio visual → CLS ≈ 0
        // El bg-gray-100 solo se muestra mientras carga → desaparece al cargar la imagen
        <div
            className={`relative overflow-hidden ${!isLoaded ? 'bg-gray-100' : ''} ${containerClassName}`}
            style={width && height ? { aspectRatio: `${width}/${height}` } : undefined}
        >
            <img
                ref={imgRef}
                src={imageSrc ?? undefined}
                alt={alt}
                width={width}
                height={height}
                decoding="async"
                className={`${className} absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setIsLoaded(true)}
            />
        </div>
    );
});

LazyImage.displayName = 'LazyImage';
